/**
 * Cart delivery-date picker (theme-only, no third-party app).
 *
 * SOW:
 *  - Auto-assign nearest open date; customer may change
 *  - Desktop tiles / mobile horizontal scroll
 *  - Max 7-day window; delivery weekdays only
 *  - Cutoff-closed tiles shown disabled (“termin zamknięty”)
 *  - Persist on order via cart attribute "Termin dostawy"
 *  - Keep selection across cart edits if still honored
 *  - §12: keep locked cart date across edits only while still open (cutoff wins)
 */
(function () {
  'use strict';

  if (window.DeliveryDatePickerInit) return;
  window.DeliveryDatePickerInit = true;

  var ATTR_DATE = 'Termin dostawy';
  var ATTR_ISO = 'Termin dostawy ISO';
  var saveQueue = Promise.resolve();
  var selectedIso = null;
  /** User tap held until /cart.js confirms — prevents flicker from stale reads. */
  var pendingEntry = null;
  var refreshTimer = null;
  var confirmTimer = null;
  var lastItemCount = 0;
  /** Bumps on every user tap / refresh start; stale async paints are ignored. */
  var paintSeq = 0;

  function ds() {
    return window.DeliverySchedule || null;
  }

  function roots() {
    return document.querySelectorAll('[data-delivery-date-picker]');
  }

  function stringsFrom(root) {
    return {
      title: root.getAttribute('data-label-title') || 'Wybierz termin dostawy',
      selected: root.getAttribute('data-label-selected') || 'Wybrany termin dostawy',
      closed: root.getAttribute('data-label-closed') || 'termin zamknięty',
      unavailable: root.getAttribute('data-label-unavailable') || 'niedostępna'
    };
  }

  function readCartAttributes() {
    return fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var attrs = (cart && cart.attributes) || {};
        return {
          display: attrs[ATTR_DATE] || '',
          iso: attrs[ATTR_ISO] || '',
          itemCount: cart.item_count || 0,
          attributes: attrs
        };
      })
      .catch(function () {
        return { display: '', iso: '', itemCount: 0, attributes: {} };
      });
  }

  function writeCartAttributes(display, iso) {
    // Queue writes — never drop a tap while a previous /cart/update is in flight.
    // Read-merge-write so we never wipe door code / terms / other attributes.
    saveQueue = saveQueue
      .catch(function () {})
      .then(function () {
        return fetch('/cart.js', {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store'
        })
          .then(function (r) {
            return r.json();
          })
          .then(function (cart) {
            var attrs = {};
            var existing = (cart && cart.attributes) || {};
            Object.keys(existing).forEach(function (key) {
              attrs[key] = existing[key];
            });
            attrs[ATTR_DATE] = display || '';
            attrs[ATTR_ISO] = iso || '';

            return fetch('/cart/update.js', {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
              },
              credentials: 'same-origin',
              body: JSON.stringify({ attributes: attrs })
            }).then(function (r) {
              if (!r.ok) throw new Error('cart update failed');
              return r.json();
            });
          });
      });

    return saveQueue;
  }

  function cartMatchesEntry(state, entry) {
    return !!(entry && state && state.iso === entry.iso && state.display === entry.display);
  }

  function clearPendingIfConfirmed(state) {
    if (pendingEntry && cartMatchesEntry(state, pendingEntry)) {
      pendingEntry = null;
    }
  }

  function resolveSelection(cartState) {
    var api = ds();
    if (!api) return null;

    // Prefer the date the shopper just tapped until /cart.js confirms.
    if (pendingEntry) return pendingEntry;

    var now = api.shopNow();
    var lockedIso = cartState.iso || '';
    if (!lockedIso && cartState.display) {
      var parsed = api.parseDisplayDate(cartState.display);
      if (parsed) lockedIso = api.toISODate(parsed);
    }

    if (lockedIso) {
      var lockedDate = api.parseDisplayDate(lockedIso) || api.parseDisplayDate(cartState.display);
      if (lockedDate && api.honorLockedDate(lockedDate, { now: now })) {
        return api.describeDate(lockedDate, { now: now, lockedIso: lockedIso });
      }
    }

    var nxt = api.next({ now: now });
    if (!nxt) return null;
    return api.describeDate(nxt.date, { now: now, lockedIso: null });
  }

  function scheduleConfirm(entry, attempt) {
    attempt = attempt || 0;
    if (confirmTimer) clearTimeout(confirmTimer);
    if (!entry) return;

    confirmTimer = setTimeout(function () {
      confirmTimer = null;
      if (!pendingEntry || pendingEntry.iso !== entry.iso) return;

      readCartAttributes().then(function (state) {
        if (!pendingEntry || pendingEntry.iso !== entry.iso) return;
        if (cartMatchesEntry(state, entry)) {
          pendingEntry = null;
          selectedIso = entry.iso;
          return;
        }
        // Cart still stale — keep optimistic UI and retry a few times.
        if (attempt < 8) scheduleConfirm(entry, attempt + 1);
      });
    }, attempt === 0 ? 200 : 400);
  }

  function ensurePersisted(entry) {
    if (!entry) return Promise.resolve(null);
    return readCartAttributes().then(function (state) {
      if (cartMatchesEntry(state, entry)) {
        clearPendingIfConfirmed(state);
        selectedIso = entry.iso;
        return entry;
      }
      return writeCartAttributes(entry.display, entry.iso).then(function (cart) {
        // Prefer the update response when it includes attributes (avoids stale /cart.js).
        var attrs = (cart && cart.attributes) || {};
        if (attrs[ATTR_ISO] === entry.iso) {
          pendingEntry = null;
          selectedIso = entry.iso;
          return entry;
        }
        // Keep pending until a later /cart.js read matches — do not paint old date.
        scheduleConfirm(entry, 0);
        selectedIso = entry.iso;
        return entry;
      });
    });
  }

  function updateCutoffHint(root, selected) {
    var cutoffEl = root.querySelector('[data-delivery-cutoff]');
    if (!cutoffEl) {
      cutoffEl = root.nextElementSibling;
      if (!cutoffEl || !cutoffEl.hasAttribute('data-delivery-cutoff')) return;
    }

    var api = ds();
    if (!api || !selected || !selected.cutoff) {
      cutoffEl.textContent = '';
      cutoffEl.hidden = true;
      return;
    }

    var now = api.shopNow();
    var text = api.formatCutoff({ cutoff: selected.cutoff, now: now });
    cutoffEl.textContent = text || '';
    cutoffEl.hidden = !text;
  }

  function renderRoot(root, windowDates, selected, labels) {
    if (!root) return;

    var list = root.querySelector('[data-delivery-tiles]');
    var summary = root.querySelector('[data-delivery-summary]');
    var title = root.querySelector('[data-delivery-picker-title]');
    if (title) title.textContent = labels.title;

    if (!list) return;

    var html = '';
    windowDates.forEach(function (slot) {
      var isSelected = selected && slot.iso === selected.iso;
      var disabled = !slot.selectable;
      var classes = 'delivery-date-picker__tile';
      if (isSelected) classes += ' is-selected';
      if (disabled) classes += ' is-closed';
      if (slot.locked && isSelected) classes += ' is-locked';

      var status = '';
      if (disabled) {
        status =
          '<span class="delivery-date-picker__status">' +
          (labels.closed || labels.unavailable) +
          '</span>';
      }

      html +=
        '<button type="button" class="' +
        classes +
        '"' +
        ' data-delivery-iso="' +
        slot.iso +
        '"' +
        ' aria-pressed="' +
        (isSelected ? 'true' : 'false') +
        '"' +
        (disabled ? ' disabled aria-disabled="true"' : '') +
        '>' +
        '<span class="delivery-date-picker__tile-short">' +
        slot.tileLabel +
        '</span>' +
        '<span class="delivery-date-picker__tile-long">' +
        slot.tileLabelLong +
        '</span>' +
        status +
        '</button>';
    });

    list.innerHTML = html;

    if (summary) {
      if (selected) {
        summary.hidden = false;
        summary.textContent = labels.selected + ': ' + selected.selectedLabel;
      } else {
        summary.hidden = true;
        summary.textContent = '';
      }
    }

    var displayInput = root.querySelector('[data-delivery-attr-display]');
    var isoInput = root.querySelector('[data-delivery-attr-iso]');
    if (displayInput) displayInput.value = selected ? selected.display : '';
    if (isoInput) isoInput.value = selected ? selected.iso : '';

    updateCutoffHint(root, selected);
    scrollSelectedIntoView(root);
  }

  function scrollSelectedIntoView(root) {
    var list = root.querySelector('[data-delivery-tiles]');
    var selectedBtn = root.querySelector('.delivery-date-picker__tile.is-selected');
    if (!list || !selectedBtn) return;

    // Keep the selected chip visible in the horizontal mobile row.
    try {
      var listRect = list.getBoundingClientRect();
      var btnRect = selectedBtn.getBoundingClientRect();
      var delta =
        btnRect.left - listRect.left - (listRect.width / 2 - btnRect.width / 2);
      list.scrollLeft += delta;
    } catch (e) {
      if (typeof selectedBtn.scrollIntoView === 'function') {
        selectedBtn.scrollIntoView({ inline: 'center', block: 'nearest' });
      }
    }
  }

  function paintAll(slots, selected, itemCount) {
    lastItemCount = itemCount;
    roots().forEach(function (root) {
      bindClicks(root);
      renderRoot(root, slots, selected, stringsFrom(root));
      root.hidden = slots.length === 0 || itemCount === 0;
    });
  }

  function paintSelection(entry, itemCount) {
    var api = ds();
    if (!api || !entry) return;
    var slots = api.listWindow({ now: api.shopNow(), lockedIso: entry.iso });
    selectedIso = entry.iso;
    paintAll(slots, entry, itemCount || lastItemCount || 1);
  }

  function bindClicks(root) {
    if (root.getAttribute('data-picker-bound') === 'true') return;
    root.setAttribute('data-picker-bound', 'true');

    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-delivery-iso]');
      if (!btn || !root.contains(btn)) return;
      if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;

      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopPropagation === 'function') e.stopPropagation();

      var iso = btn.getAttribute('data-delivery-iso');
      if (!iso || iso === selectedIso) return;

      var api = ds();
      if (!api) return;
      var date = api.parseDisplayDate(iso);
      if (!date) return;

      // New picks must still be open (cutoff not passed). Already-selected
      // locked days exit earlier via iso === selectedIso.
      if (!api.isDateAvailable(date)) return;

      var entry = api.describeDate(date, { lockedIso: iso });
      paintSeq += 1;
      pendingEntry = entry;
      selectedIso = entry.iso;

      // Optimistic UI — stay on this date until cart confirms (no interim revert).
      paintSelection(entry, lastItemCount || 1);

      ensurePersisted(entry).catch(function () {
        // Roll back only if this tap is still the pending one.
        if (pendingEntry && pendingEntry.iso === entry.iso) {
          pendingEntry = null;
          scheduleRefresh(0);
        }
      });
    });
  }

  var scheduleWaitTries = 0;

  function refresh() {
    var api = ds();
    // Schedule script may still be loading — retry briefly instead of staying blank.
    if (!api) {
      if (scheduleWaitTries < 40) {
        scheduleWaitTries += 1;
        return scheduleRefresh(100);
      }
      return Promise.resolve();
    }
    scheduleWaitTries = 0;

    var nodes = roots();
    if (!nodes.length) return Promise.resolve();

    var seq = ++paintSeq;

    return readCartAttributes().then(function (cartState) {
      if (seq !== paintSeq) return;

      lastItemCount = cartState.itemCount;
      clearPendingIfConfirmed(cartState);

      var selected = resolveSelection(cartState);

      // While a tap is in flight, never re-persist / re-paint the old cart date.
      if (pendingEntry) {
        if (seq !== paintSeq) return;
        paintSelection(pendingEntry, cartState.itemCount);
        return ensurePersisted(pendingEntry);
      }

      var persist = Promise.resolve(selected);
      if (selected && cartState.itemCount > 0) {
        persist = ensurePersisted(selected);
      } else if (!selected && (cartState.iso || cartState.display)) {
        persist = writeCartAttributes('', '').then(function () {
          return null;
        });
      }

      return persist.then(function (finalSelected) {
        if (seq !== paintSeq) return;
        // If a tap landed during persist, keep the tap visible.
        if (pendingEntry) {
          paintSelection(pendingEntry, cartState.itemCount);
          return;
        }
        selectedIso = finalSelected ? finalSelected.iso : null;
        var slots = api.listWindow({
          now: api.shopNow(),
          lockedIso: selectedIso
        });
        paintAll(slots, finalSelected, cartState.itemCount);
      });
    });
  }

  function scheduleRefresh(delay) {
    if (refreshTimer) clearTimeout(refreshTimer);
    return new Promise(function (resolve) {
      refreshTimer = setTimeout(function () {
        refreshTimer = null;
        resolve(refresh());
      }, delay || 0);
    });
  }

  function start() {
    refresh();
    setInterval(function () {
      refresh();
    }, 30 * 1000);

    ['cart:refresh', 'cart:update', 'cart:change'].forEach(function (name) {
      document.addEventListener(name, function () {
        // Don't let cart events snap UI back to a stale attribute mid-save.
        if (pendingEntry) {
          scheduleConfirm(pendingEntry, 0);
          return;
        }
        scheduleRefresh(50);
      });
    });

    if (typeof subscribe === 'function') {
      try {
        subscribe('cart-update', function () {
          if (pendingEntry) {
            scheduleConfirm(pendingEntry, 0);
            return;
          }
          scheduleRefresh(50);
        });
      } catch (e) {
        /* optional */
      }
    }

    // When drawer/cart HTML is swapped in via AJAX, re-bind + paint.
    if (window.MutationObserver && document.body) {
      var moQueued = false;
      new MutationObserver(function () {
        if (moQueued) return;
        moQueued = true;
        window.requestAnimationFrame(function () {
          moQueued = false;
          if (!roots().length) return;
          scheduleRefresh(0);
        });
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  window.DeliveryDatePicker = {
    refresh: refresh,
    attrDate: ATTR_DATE,
    attrIso: ATTR_ISO
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
